import { useShapeService } from '@/api/services/shapeService';
import { type Subproject as CardSubproject } from '@/organization/landing/components/projectCard/types';
import { getOverallStatus } from '@/organization/landing/components/projectCard/utils';
import { useOrg } from '@/organization/useOrg';
import { useGetProject } from '@/queries/useProjects.ts';
import {
  alpha,
  Box,
  Button,
  Container,
  Grow,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FooterAppBar } from '../components/HeaderAppBar.tsx';
import { Routes } from '../CSIRouter.tsx';
import { useColorWheel } from '../hooks/useColorWheel.ts';
import { type Project } from '../stores/ProjectStore.ts';
import {
  SubprojectBuildingDetails,
  useSubprojectStore,
} from '../stores/SubprojectStore.ts';
import { formattedNumber } from '../utils.ts';
import { BoApprovalModal } from './components/BoApprovalModal.tsx';
import { DecarbHeaderBar } from './components/DecarbHeaderBar.tsx';
import { PmPreferenceModal } from './components/PmPreferenceModal.tsx';
import { StageStatusButton } from './components/StageStatusButton';
import { DecarbOverviewTile } from './DecarbOverviewTile.tsx';

const ALTERNATIVE_TO_USAGE: Record<string, string> = {
  conventional: 'Conventional',
  'reduced-carbon': 'Low Carbon',
  'zero-carbon': 'Net Zero',
};

// Demo reports are static for now; serve the one matching the project's country.
const REPORT_PATH_BY_COUNTRY: Record<string, string> = {
  CA: '/report/report_ca.pdf',
};
const DEFAULT_REPORT_PATH = '/report/report.pdf';

export const DecarbOverview = () => {
  const subprojectStore = useSubprojectStore();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { selectedOrg } = useOrg();
  const [subprojects, setSubprojects] = useState<SubprojectBuildingDetails[]>(
    () => [],
  );

  const { data: project } = useGetProject(selectedOrg?.id, projectId);
  const shapeService = useShapeService();
  const [reportPath, setReportPath] = useState<string>(DEFAULT_REPORT_PATH);
  const [detailed, setDetailed] = useState<SubprojectBuildingDetails[]>([]);
  const { getColor } = useColorWheel();
  const [pmPreferenceModalOpen, setPmPreferenceModalOpen] = useState(false);
  const [boApprovalModalOpen, setBoApprovalModalOpen] = useState(false);

  const pmPreferredUsage = project?.designApprovedAlternative
    ? ALTERNATIVE_TO_USAGE[project.designApprovedAlternative]
    : null;

  const boApprovedUsage = project?.budgetApprovedAlternative
    ? ALTERNATIVE_TO_USAGE[project.budgetApprovedAlternative]
    : null;

  const refreshSubprojects = useCallback(() => {
    if (!projectId) return;
    subprojectStore
      .fetchSubprojects(projectId)
      .then(setSubprojects)
      .catch(console.error);
  }, [subprojectStore, projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    subprojectStore
      .fetchSubprojects(projectId)
      .then(setSubprojects)
      .catch(console.error);
  }, [subprojectStore, projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    shapeService
      .list(projectId)
      .then((response) => {
        const countryCode = (response.data?.features ?? []).find(
          (f) => f.properties.countryCode,
        )?.properties.countryCode;
        setReportPath(
          (countryCode && REPORT_PATH_BY_COUNTRY[countryCode]) ||
            DEFAULT_REPORT_PATH,
        );
      })
      .catch(console.error);
  }, [shapeService, projectId]);

  useEffect(() => {
    if (!subprojects || !selectedOrg) {
      return;
    }

    Promise.allSettled(
      subprojects.map((s) =>
        subprojectStore.fetchSubprojectDetails(s.id, selectedOrg.id),
      ),
    )
      .then((settlements) => {
        const sps: SubprojectBuildingDetails[] = [];
        for (const settlement of settlements) {
          switch (settlement.status) {
            case 'fulfilled':
              sps.push(settlement.value);
              break;
            case 'rejected':
            default:
              console.error('Unable to acquire details for subproject');
          }
        }
        setDetailed(sps);
      })
      .catch((e) => {
        console.error(e);
        setDetailed([]);
      });
  }, [subprojectStore, subprojects, selectedOrg]);

  const designLifeDisplay = React.useMemo(() => {
    const lifes = detailed
      .map((sp) => sp.designLifeYears)
      .filter((v): v is number => v != null);
    if (lifes.length === 0) return null;
    if (lifes.length === 1) return `${formattedNumber(lifes[0])} yrs`;
    return `${formattedNumber(Math.min(...lifes))}–${formattedNumber(Math.max(...lifes))} yrs`;
  }, [detailed]);

  // module scope
  const sumOf = (
    sps: SubprojectBuildingDetails[],
    pick: (sp: SubprojectBuildingDetails) => number | null | undefined,
  ) => sps.reduce((total, sp) => total + Number(pick(sp) ?? 0), 0);

  // in the component
  const { designCostSum, constructionCostSum } = React.useMemo(
    () => ({
      designCostSum: sumOf(detailed, (sp) => sp.designCost),
      constructionCostSum: sumOf(detailed, (sp) => sp.constructionCost),
    }),
    [detailed],
  );

  const newOrRenewalDisplay = React.useMemo(() => {
    const vals = [
      ...new Set(
        detailed
          .map((sp) => sp.newOrRenewal)
          .filter((v): v is 'new' | 'renewal' => v != null),
      ),
    ];
    if (vals.length === 0) return null;
    if (vals.length === 1) return vals[0] === 'new' ? 'New' : 'Renewal';
    return 'Mixed';
  }, [detailed]);

  return (
    <React.Fragment>
      <Container maxWidth='xl' sx={{ px: 0, overflow: 'auto' }}>
        <DecarbHeaderBar
          orgName={selectedOrg?.name || ''}
          projectName={project?.name || ''}
          fiscalYearName={project?.fiscalYearDetails?.name}
          subprojects={subprojects}
          currentSubprojectId={undefined}
          projectId={projectId}
          pencilsDown={project?.pencilsDown}
          designApproved={project?.designApproved}
          budgetApproved={project?.budgetApproved}
          onPmPreferenceClick={() => setPmPreferenceModalOpen(true)}
          onBoApprovalClick={() => setBoApprovalModalOpen(true)}
          onPencilsDownToggle={refreshSubprojects}
          onSubprojectChange={(id) => {
            if (id === 'overview') return; // already on overview
            navigate(`/project/${projectId}/decarb/${id}`);
          }}
        />
        <Grid
          container
          columnSpacing={{ xs: 1, sm: 2 }}
          rowSpacing={{ xs: 2, sm: 1 }}
        >
          <Grid size={{ md: 4, sm: 12 }}>
            <DecarbOverviewTile
              usage={'Conventional'}
              getColor={getColor}
              alternatives={detailed.map((a) => a.conventionalAlternative)}
              compares={[]}
              pmPreferred={'Conventional' === pmPreferredUsage}
              boApproved={'Conventional' === boApprovedUsage}
              designLifeDisplay={designLifeDisplay}
              newOrRenewalDisplay={newOrRenewalDisplay}
	      constructionCostSum={constructionCostSum}
	      designCostSum={designCostSum}
            />
          </Grid>
          <Grid size={{ md: 4, sm: 12 }}>
            <DecarbOverviewTile
              usage={'Low Carbon'}
              getColor={getColor}
              alternatives={detailed.map((a) => a.reducedCarbonAlternative)}
              compares={detailed.map((a) => a.conventionalAlternative)}
              pmPreferred={'Low Carbon' === pmPreferredUsage}
              boApproved={'Low Carbon' === boApprovedUsage}
              designLifeDisplay={designLifeDisplay}
              newOrRenewalDisplay={newOrRenewalDisplay}
	      constructionCostSum={constructionCostSum}
	      designCostSum={designCostSum}
            />
          </Grid>
          <Grid size={{ md: 4, sm: 12 }}>
            <DecarbOverviewTile
              usage={'Net Zero'}
              getColor={getColor}
              alternatives={detailed.map((a) => a.zeroCarbonAlternative)}
              compares={detailed.map((a) => a.conventionalAlternative)}
              pmPreferred={'Net Zero' === pmPreferredUsage}
              boApproved={'Net Zero' === boApprovedUsage}
              designLifeDisplay={designLifeDisplay}
              newOrRenewalDisplay={newOrRenewalDisplay}
	      constructionCostSum={constructionCostSum}
	      designCostSum={designCostSum}
            />
          </Grid>
        </Grid>
      </Container>
      {projectId && (
        <PmPreferenceModal
          open={pmPreferenceModalOpen}
          onClose={() => setPmPreferenceModalOpen(false)}
          tiles={[
            {
              usage: 'Conventional',
              alternatives: detailed.map((a) => a.conventionalAlternative),
              compares: [],
            },
            {
              usage: 'Low Carbon',
              alternatives: detailed.map((a) => a.reducedCarbonAlternative),
              compares: detailed.map((a) => a.conventionalAlternative),
            },
            {
              usage: 'Net Zero',
              alternatives: detailed.map((a) => a.zeroCarbonAlternative),
              compares: detailed.map((a) => a.conventionalAlternative),
            },
          ]}
          getColor={getColor}
          currentPreference={project?.designApprovedAlternative}
          projectId={projectId}
        />
      )}
      {projectId && (
        <BoApprovalModal
          open={boApprovalModalOpen}
          onClose={() => setBoApprovalModalOpen(false)}
          tiles={[
            {
              usage: 'Conventional',
              alternatives: detailed.map((a) => a.conventionalAlternative),
              compares: [],
            },
            {
              usage: 'Low Carbon',
              alternatives: detailed.map((a) => a.reducedCarbonAlternative),
              compares: detailed.map((a) => a.conventionalAlternative),
            },
            {
              usage: 'Net Zero',
              alternatives: detailed.map((a) => a.zeroCarbonAlternative),
              compares: detailed.map((a) => a.conventionalAlternative),
            },
          ]}
          getColor={getColor}
          currentApproval={project?.budgetApprovedAlternative}
          projectId={projectId}
        />
      )}
      <DecarbOverviewFooter
        subprojects={subprojects}
        project={project}
        reportPath={reportPath}
      />
    </React.Fragment>
  );
};

interface DecarbOverviewFooterProps {
  subprojects: SubprojectBuildingDetails[];
  project?: Project | null;
  reportPath: string;
}

const DecarbOverviewFooter: React.FC<DecarbOverviewFooterProps> = ({
  subprojects,
  project,
  reportPath,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showModal, setShowModal] = useState<boolean>(false);

  const allSubjectsComplete =
    subprojects.length > 0 &&
    subprojects.every(
      (s) => getOverallStatus(s as unknown as CardSubproject) === 'finished',
    );
  const isBoApproved = project?.budgetApproved === true;

  return (
    <React.Fragment>
      <FooterAppBar
        position={'sticky'}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          pb: { xs: 1, md: 2 },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'center', md: 'space-between' },
            gap: { xs: 2, md: 0 },
            py: { xs: 1, md: 0 },
          }}
        >
          <Button
            variant={'contained'}
            color={'success'}
            size={isMobile ? 'small' : 'medium'}
            disabled={project?.pencilsDown}
            sx={{
              borderRadius: 1,
              py: { xs: 1.5, md: 3 },
              px: { xs: 3, md: 8 },
              width: { xs: '100%', sm: 'auto' },
            }}
            onClick={() => {
              navigate(
                '../' + Routes.Project.SubprojectCreate.path,
              );
            }}
          >
            Add New Subproject
          </Button>
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, 0.4),
              p: { xs: 1, md: 2 },
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: { xs: 1, md: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <StageStatusButton
              label='Subprojects Complete'
              isComplete={allSubjectsComplete}
              sx={{ minWidth: { xs: 'auto', sm: 160 } }}
            />
            <StageStatusButton
              label='BO Approved'
              isComplete={isBoApproved}
              sx={{ minWidth: { xs: 'auto', sm: 160 } }}
            />
          </Box>
          <Button
            variant={'contained'}
            color={'success'}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderRadius: 1,
              py: { xs: 1.5, md: 3 },
              px: { xs: 3, md: 8 },
              width: { xs: '100%', sm: 'auto' },
            }}
            onClick={() => {
              setShowModal(true);
            }}
          >
            Review & Export
          </Button>
        </Toolbar>
      </FooterAppBar>
      {showModal && (
        <ReviewModalOverlay
          reportPath={reportPath}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

interface ReviewModalOverlayProps {
  reportPath: string;
  onClose: () => void;
}
const ReviewModalOverlay: React.FC<ReviewModalOverlayProps> = ({
  reportPath,
  onClose,
}) => {
  const theme = useTheme();
  const [hide, setHide] = useState<boolean>(false);

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.info.main, 0.8),
        px: 0,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center',
        '& > div': {
          width: 820,
        },
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
      onClick={(e) => {
        setHide(true);
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Grow in={!hide}>
        <Paper
          elevation={0}
          sx={{ display: 'flex', p: 2, alignItems: 'center', gap: 2 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Typography variant={'subtitle1'} sx={{ textTransform: 'uppercase' }}>
            Report Generated
          </Typography>
          <Button
            variant={'contained'}
            color={'info'}
            sx={{ ml: 'auto' }}
            onClick={() => {
              const element = document.getElementById(
                'carb-report',
              ) as HTMLObjectElement;
              if (!element) {
                console.error('unable to locate decarb report element');
              }

              if (element.contentWindow) {
                element.contentWindow.print();
              } else {
                console.error('Unable to print content window, window is null');
              }
            }}
          >
            Print
          </Button>
          <Button
            component={'a'}
            variant={'contained'}
            color={'info'}
            href={reportPath}
            target={'_blank'}
            download={'report.pdf'}
          >
            Download
          </Button>
        </Paper>
      </Grow>
      <Grow
        in={!hide}
        onExited={() => {
          onClose();
        }}
      >
        <Paper
          sx={{
            flex: 1,
            flexGrow: 1,
            flexShrink: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* iFrames allow the print() call to flow through... */}
          <iframe
            style={{ flex: 1, flexGrow: 1, border: 0 }}
            src={`${reportPath}#toolbar=0&view=FitH`}
            id={'carb-report'}
          />
        </Paper>
      </Grow>
    </Box>
  );
};
